import type {
  NodeRendererContext,
  LinkRendererContext,
  States,
  Bounds,
  DiagramLabelPosition,
  DiagramNodeData,
  DiagramLinkData
} from './diagram.types';
import { DiagramNodeLayer } from './DiagramNodeLayer';
import { DiagramLinkLayer } from './DiagramLinkLayer';
import { DiagramLabelLayer } from './DiagramLabelLayer';
import { ComponentChildren } from 'preact';
import type { TextDimensions } from '../hooks/PRIVATE_useTextDimensions';

/**
 * Props for Diagram layers component
 */
type DiagramLayersProps<K1, K2, D1, D2> = {
  nodes: D1[];
  links: D2[];
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren;
  linkRenderer?: (context: LinkRendererContext<D2>) => ComponentChildren;
  states: States<K1, K2>;
  previousStates: States<K1, K2>;
  nodeBounds: Bounds[];
  // width height are for svg for links
  width: number;
  height: number;
  onNodeSizeChanged: (width: number, height: number, idx: number) => void;
  nodeLabelPositions?: (DiagramLabelPosition | undefined)[];
  linkLabelPositions?: (DiagramLabelPosition | undefined)[];
  labelDimensions?: Map<K1 | K2, TextDimensions>;
  supportsSelection: boolean;
};

export function DiagramLayers<
  K1,
  K2,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>
>({
  nodes,
  links,
  nodeRenderer,
  linkRenderer,
  states,
  previousStates,
  nodeBounds,
  onNodeSizeChanged,
  width,
  height,
  nodeLabelPositions,
  linkLabelPositions,
  labelDimensions,
  supportsSelection
}: DiagramLayersProps<K1, K2, D1, D2>) {
  return (
    <>
      {links.length > 0 && linkRenderer && width !== Infinity && (
        <DiagramLinkLayer
          links={links}
          states={states}
          previousStates={previousStates}
          linkRenderer={linkRenderer}
          width={width}
          height={height}
          supportsSelection={supportsSelection}
        />
      )}
      <DiagramNodeLayer
        nodes={nodes}
        nodeBounds={nodeBounds}
        states={states}
        previousStates={previousStates}
        nodeRenderer={nodeRenderer}
        onNodeSizeChanged={onNodeSizeChanged}
        supportsSelection={supportsSelection}
      />
      {labelDimensions ? (
        <div>
          {nodeLabelPositions ? (
            <DiagramLabelLayer
              labelPosition={nodeLabelPositions}
              itemProps={nodes}
              labelDimensions={labelDimensions}
            />
          ) : undefined}
          {links && linkLabelPositions ? (
            <DiagramLabelLayer
              labelPosition={linkLabelPositions}
              itemProps={links}
              labelDimensions={labelDimensions}
            />
          ) : undefined}
        </div>
      ) : undefined}
    </>
  );
}
